import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Chip, IconButton, Tooltip, CircularProgress, Alert, Checkbox, FormControlLabel, Link,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { adminClient } from '../data/adminClient';
import { SIZE_OPTIONS, FILTER_LEVELS, FILTER_STYLES } from '../../shared/entryOptions';
import ChipFilterRow from './ChipFilterRow';
import TestSchoolChip from '../TestSchoolChip';

// Browse every entry across every school and play/download its music — the
// staff/admin side of entry music screening (see the teacher-side upload in
// EntryLibrary.js). Sortable/filterable table, same pattern as
// SchoolRegistrationsSection, with an inline play/stop control per row.
export default function EntryRegistrationsSection() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('All');
  const [levelFilter, setLevelFilter] = useState(() => new Set());
  const [sizeFilter, setSizeFilter] = useState(() => new Set());
  const [styleFilter, setStyleFilter] = useState('All');
  const [missingOnly, setMissingOnly] = useState(false);
  const [hideFreestyle, setHideFreestyle] = useState(false);
  const [propsOnly, setPropsOnly] = useState(false);
  const [sortField, setSortField] = useState('schoolName');
  const [sortDir, setSortDir] = useState('asc');

  // Only one track plays at a time — starting a new one stops whatever's
  // already playing. audioRef holds the live Audio object (not rendered —
  // just a small icon-button per row, not a visible <audio> element).
  const [playingKey, setPlayingKey] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [playError, setPlayError] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [pendingHidden, setPendingHidden] = useState(0); // rows hidden: school's payment pending
  const audioRef = useRef(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allEntries, registrations] = await Promise.all([
        adminClient.getEntries(),
        adminClient.getEventRegistrations(),
      ]);
      // Withdrawn schools already paid their (non-refundable) registration
      // fee but aren't attending — their entries stay on the sheet for the
      // record, but shouldn't clutter this screening view. Still fully
      // visible on the School Payments page.
      // Schools still waiting on their registration payment (paymentPending,
      // set server-side) don't show here until paid or OK to Proceed.
      const withdrawnSchools = new Set(registrations.filter(r => r.status === 'Withdrawn').map(r => r.schoolName));
      const active = allEntries.filter(e => !withdrawnSchools.has(e.schoolName));
      setEntries(active.filter(e => !e.paymentPending));
      setPendingHidden(active.filter(e => e.paymentPending).length);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Stop playback if this page unmounts (e.g. navigating to another section).
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const keyOf = (e) => `${e.schoolName}::${e.title}`;

  const schoolNames = useMemo(() => [...new Set(entries.map(e => e.schoolName))].filter(Boolean).sort(), [entries]);

  const displayRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = entries.filter(e =>
      (!q || `${e.schoolName} ${e.title}`.toLowerCase().includes(q)) &&
      (schoolFilter === 'All' || e.schoolName === schoolFilter) &&
      (levelFilter.size === 0 || levelFilter.has(e.level)) &&
      (sizeFilter.size === 0 || sizeFilter.has(e.size)) &&
      (styleFilter === 'All' || e.style === styleFilter) &&
      (!missingOnly || !e.music) &&
      (!hideFreestyle || e.level !== 'Freestyle') &&
      (!propsOnly || !!e.props)
    );
    return [...list].sort((a, b) => {
      if (sortField === 'numOfDancers') {
        const cmp = (Number(a.numOfDancers) || 0) - (Number(b.numOfDancers) || 0);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const cmp = String(a[sortField] || '').toLowerCase().localeCompare(String(b[sortField] || '').toLowerCase());
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [entries, search, schoolFilter, levelFilter, sizeFilter, styleFilter, missingOnly, hideFreestyle, propsOnly, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleSetFilter = (setter) => (value) => setter(prev => {
    const next = new Set(prev);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  });
  const toggleLevel = toggleSetFilter(setLevelFilter);
  const toggleSize = toggleSetFilter(setSizeFilter);

  const hasActiveFilters = !!search || schoolFilter !== 'All' || levelFilter.size > 0 || sizeFilter.size > 0 ||
    styleFilter !== 'All' || missingOnly || hideFreestyle || propsOnly;

  const clearFilters = () => {
    setSearch('');
    setSchoolFilter('All');
    setLevelFilter(new Set());
    setSizeFilter(new Set());
    setStyleFilter('All');
    setMissingOnly(false);
    setHideFreestyle(false);
    setPropsOnly(false);
  };

  const stopPlayback = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingKey(null);
  };

  const handleTogglePlay = async (entry) => {
    const key = keyOf(entry);
    setPlayError(null);

    if (playingKey === key) { stopPlayback(); return; }
    stopPlayback(); // switching tracks — stop whatever's currently playing first
    if (!entry.music) return;

    setLoadingKey(key);
    try {
      const { url } = await adminClient.getEntryMusicUrl(entry.schoolName, entry.title);

      const audio = new Audio(url);
      audio.onended = () => setPlayingKey((k) => (k === key ? null : k));
      audio.onerror = () => { setPlayError('Playback failed.'); setPlayingKey((k) => (k === key ? null : k)); };
      audioRef.current = audio;
      await audio.play();
      setPlayingKey(key);
    } catch (err) {
      setPlayError(err.message);
    } finally {
      setLoadingKey(null);
    }
  };

  const handleDownload = async (entry) => {
    if (!entry.music) return;
    const key = keyOf(entry);
    setPlayError(null);
    setDownloadingKey(key);
    try {
      const { url } = await adminClient.getEntryMusicUrl(entry.schoolName, entry.title, { download: true });
      // The presigned URL itself carries a Content-Disposition: attachment
      // header (set server-side), so just navigating to it triggers a save
      // dialog rather than opening the file inline.
      window.open(url, '_blank');
    } catch (err) {
      setPlayError(err.message);
    } finally {
      setDownloadingKey(null);
    }
  };

  // Test-school entries (isTest) are listed but never counted.
  const countedEntries = entries.filter(e => !e.isTest);
  const missingCount = countedEntries.filter(e => !e.music).length;
  const countedShown = displayRows.filter(e => !e.isTest).length;
  const testShown = displayRows.length - countedShown;

  const TABLE_COLS = [
    { label: 'School', field: 'schoolName' },
    { label: 'Title', field: 'title' },
    { label: 'Level', field: 'level' },
    { label: 'Style', field: 'style' },
    { label: 'Size', field: 'size' },
    { label: 'Dancers', field: 'numOfDancers' },
    { label: 'Start Position', field: 'startPosition' },
  ];

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.3, verticalAlign: 'middle', ml: 0.25 }} />;
    return sortDir === 'asc'
      ? <KeyboardArrowUpIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.25 }} />
      : <KeyboardArrowDownIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.25 }} />;
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search school or entry..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>School</InputLabel>
          <Select label="School" value={schoolFilter} onChange={e => setSchoolFilter(e.target.value)}>
            <MenuItem value="All">All schools</MenuItem>
            {schoolNames.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Style</InputLabel>
          <Select label="Style" value={styleFilter} onChange={e => setStyleFilter(e.target.value)}>
            <MenuItem value="All">All styles</MenuItem>
            {FILTER_STYLES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => adminClient.openEntriesMusicZip()}
          disabled={entries.every(e => !e.music)}
          sx={{ ml: 'auto' }}
        >
          Download all music (.zip)
        </Button>
      </Box>

      <ChipFilterRow label="Level" options={FILTER_LEVELS} selected={levelFilter} onToggle={toggleLevel} />
      <ChipFilterRow label="Size" options={SIZE_OPTIONS} selected={sizeFilter} onToggle={toggleSize} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Checkbox size="small" checked={missingOnly} onChange={e => setMissingOnly(e.target.checked)} />}
          label={<Typography variant="body2">Missing music</Typography>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={hideFreestyle} onChange={e => setHideFreestyle(e.target.checked)} />}
          label={<Typography variant="body2">Hide Freestyle (no music needed)</Typography>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={propsOnly} onChange={e => setPropsOnly(e.target.checked)} />}
          label={<Typography variant="body2">Has props</Typography>}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {countedShown} of {countedEntries.length} entries · {missingCount} missing music
        {testShown > 0 && ` (+ ${testShown} from test schools, not counted)`}
        {pendingHidden > 0 && ` · ${pendingHidden} more hidden until their school's registration payment is received`}
        {hasActiveFilters && (
          <>
            {' · '}
            <Link component="button" variant="body2" underline="hover" onClick={clearFilters} sx={{ cursor: 'pointer' }}>
              Clear filters
            </Link>
          </>
        )}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {playError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPlayError(null)}>{playError}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {TABLE_COLS.map(({ label, field }) => (
                  <TableCell key={field} onClick={() => handleSort(field)}
                    sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {label}<SortIcon field={field} />
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600 }}>Props</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Music</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {entries.length === 0 ? 'No entries yet.' : 'No matching entries.'}
                  </TableCell>
                </TableRow>
              )}
              {displayRows.map(entry => {
                const key = keyOf(entry);
                const isPlaying = playingKey === key;
                const isLoadingThis = loadingKey === key;
                const isDownloadingThis = downloadingKey === key;
                return (
                  <TableRow key={key} hover selected={isPlaying}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.schoolName}{entry.isTest && <TestSchoolChip />}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.title}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.level}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.style}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.size}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.numOfDancers}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.startPosition || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      {entry.props ? (
                        <Tooltip title={entry.props}>
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {entry.props}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {entry.music ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title={isPlaying ? 'Stop' : 'Play'}>
                            <span>
                              <IconButton
                                size="small"
                                color={isPlaying ? 'error' : 'primary'}
                                onClick={() => handleTogglePlay(entry)}
                                disabled={isLoadingThis}
                              >
                                {isLoadingThis
                                  ? <CircularProgress size={16} />
                                  : isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Download">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(entry)}
                                disabled={isDownloadingThis}
                              >
                                {isDownloadingThis ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Chip label="Missing" color="warning" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
